if (!customElements.get('wse-measurement-picker')) {
  class WseMeasurementPicker extends HTMLElement {
    static CATEGORY_ORDER = ['child', 'women', 'men'];

    connectedCallback() {
      this.variantSelects = this.closest('variant-selects');
      this.hiddenSize = this.querySelector('[data-wse-hidden-size]');
      this.hiddenFit = this.querySelector('[data-wse-hidden-fit]');
      this.displayPicker = this.querySelector('[data-wse-display-picker]');
      this.categoryTabs = this.querySelector('[data-wse-category-tabs]');
      this.sectionId = this.dataset.section;

      if (this.displayPicker) {
        this.displayPicker.addEventListener('change', this.onDisplayChange.bind(this));
      }

      if (this.categoryTabs) {
        this.categoryTabs.addEventListener('change', this.onCategoryChange.bind(this));
      }

      this.bindChartRows();
      this.bindModalScroll();
      this.syncDisplayFromHidden();
      this.initCategoryTabs();
    }

    getVariantChoices() {
      return Array.from(this.displayPicker?.querySelectorAll('[data-wse-variant-choice]') || []);
    }

    getCategoryFromFitValue(fitValue) {
      const label = (fitValue || '').split(' - ')[0] || '';
      if (/children|child/i.test(label)) return 'child';
      if (/women|woman/i.test(label)) return 'women';
      if (/^men/i.test(label)) return 'men';
      return 'men';
    }

    getDefaultCategory() {
      for (const category of WseMeasurementPicker.CATEGORY_ORDER) {
        const hasAvailable = this.getVariantChoices().some(
          (input) => input.dataset.wseCategory === category && !input.classList.contains('disabled')
        );
        if (hasAvailable) return category;
      }
      return 'child';
    }

    getCheckedCategoryTab() {
      return this.categoryTabs?.querySelector('[data-wse-category-tab]:checked')?.value || null;
    }

    setActiveCategory(category, { autoSelect = true } = {}) {
      const tab = this.categoryTabs?.querySelector(`[data-wse-category-tab][value="${category}"]`);
      if (tab) tab.checked = true;

      this.getVariantChoices().forEach((input) => {
        const isVisible = input.dataset.wseCategory === category;
        input.classList.toggle('wse-size-picker__input--hidden', !isVisible);
      });

      if (!autoSelect) return;

      const checked = this.getVariantChoices().find((input) => input.checked);
      if (checked && checked.dataset.wseCategory === category) return;

      const firstAvailable = this.getVariantChoices().find(
        (input) => input.dataset.wseCategory === category && !input.classList.contains('disabled')
      );
      const fallback = this.getVariantChoices().find((input) => input.dataset.wseCategory === category);

      const nextInput = firstAvailable || fallback;
      if (nextInput) {
        nextInput.checked = true;
        this.applySelection(nextInput.dataset.sizeValue, nextInput.dataset.fitValue, nextInput.value);
      }
    }

    initCategoryTabs() {
      if (!this.categoryTabs) return;

      const checkedChoice = this.getVariantChoices().find((input) => input.checked);
      const category = checkedChoice
        ? checkedChoice.dataset.wseCategory
        : this.getDefaultCategory();

      this.setActiveCategory(category, { autoSelect: false });
    }

    onCategoryChange(event) {
      const input = event.target;
      if (!input.matches('[data-wse-category-tab]')) return;
      this.setActiveCategory(input.value);
    }

    onDisplayChange(event) {
      const input = event.target;
      if (!input.matches('[data-wse-variant-choice]')) return;

      const category = input.dataset.wseCategory;
      if (category && this.getCheckedCategoryTab() !== category) {
        this.setActiveCategory(category, { autoSelect: false });
      }

      this.applySelection(input.dataset.sizeValue, input.dataset.fitValue, input.value);
    }

    applySelection(sizeValue, fitValue, variantId) {
      const sizeRadio = this.findOptionRadio(this.hiddenSize, sizeValue);
      const fitRadio = this.findOptionRadio(this.hiddenFit, fitValue);

      if (sizeRadio) sizeRadio.checked = true;
      if (fitRadio) {
        fitRadio.checked = true;
        fitRadio.dispatchEvent(new Event('change', { bubbles: true }));
      }

      this.highlightChartRow(variantId);
    }

    findOptionRadio(container, value) {
      if (!container || value == null) return null;
      return Array.from(container.querySelectorAll('input[type="radio"]')).find((input) => input.value === value);
    }

    syncDisplayFromHidden() {
      const checkedFit = this.hiddenFit?.querySelector('input[type="radio"]:checked');
      const checkedSize = this.hiddenSize?.querySelector('input[type="radio"]:checked');
      if (!checkedFit || !checkedSize) return;

      const displayInput = this.getVariantChoices().find(
        (input) =>
          input.dataset.sizeValue === checkedSize.value && input.dataset.fitValue === checkedFit.value
      );

      if (displayInput) {
        displayInput.checked = true;
        this.setActiveCategory(displayInput.dataset.wseCategory, { autoSelect: false });
        this.highlightChartRow(displayInput.value);
      }
    }

    highlightChartRow(variantId) {
      const modal = document.querySelector(`#WseSizeChartModal-${this.sectionId}`);
      if (!modal) return;
      modal.querySelectorAll('[data-wse-chart-row]').forEach((row) => {
        const isCurrent = row.dataset.variantId === String(variantId);
        row.setAttribute('aria-current', isCurrent ? 'true' : 'false');
        row.classList.toggle('wse-size-chart-table__row--selected', isCurrent);
      });
    }

    bindChartRows() {
      const modal = document.querySelector(`#WseSizeChartModal-${this.sectionId}`);
      if (!modal || modal.dataset.wseChartBound === 'true') return;
      modal.dataset.wseChartBound = 'true';

      modal.addEventListener('click', (event) => {
        const row = event.target.closest('[data-wse-chart-row]');
        if (!row || row.classList.contains('wse-size-chart-table__row--unavailable')) return;

        const displayInput = this.displayPicker?.querySelector(
          `[data-wse-variant-choice][value="${row.dataset.variantId}"]`
        );
        if (displayInput) {
          if (displayInput.dataset.wseCategory) {
            this.setActiveCategory(displayInput.dataset.wseCategory, { autoSelect: false });
          }
          displayInput.checked = true;
          displayInput.dispatchEvent(new Event('change', { bubbles: true }));
        } else {
          const category = this.getCategoryFromFitValue(row.dataset.fitValue);
          this.setActiveCategory(category, { autoSelect: false });
          this.applySelection(row.dataset.sizeValue, row.dataset.fitValue, row.dataset.variantId);
        }

        if (typeof modal.hide === 'function') modal.hide();
      });
    }

    bindModalScroll() {
      const modal = document.querySelector(`#WseSizeChartModal-${this.sectionId}`);
      if (!modal || modal.dataset.wseScrollBound === 'true') return;
      modal.dataset.wseScrollBound = 'true';

      const scrollToSection = () => {
        const sectionId = modal.dataset.wseGuideSection;
        if (!sectionId) return;

        modal.querySelectorAll('.wse-size-guide__section-title--active').forEach((title) => {
          title.classList.remove('wse-size-guide__section-title--active');
        });

        const section = modal.querySelector(`#${sectionId}`);
        if (!section) return;

        section.classList.add('wse-size-guide__section-title--active');
        section.scrollIntoView({ behavior: 'smooth', block: 'start' });
      };

      const observer = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          if (mutation.attributeName === 'open' && modal.hasAttribute('open')) {
            window.requestAnimationFrame(scrollToSection);
          }
        }
      });

      observer.observe(modal, { attributes: true, attributeFilter: ['open'] });
    }
  }

  customElements.define('wse-measurement-picker', WseMeasurementPicker);
}
